import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Lotus Esprit S1 (~1977 — the Giugiaro "folded paper" origami wedge, the
// submarine car) at true scale (1 unit = 1 metre): length ~4.19, width 1.86,
// height ~1.11 (extremely LOW), wheelbase 2.44, on ~0.31 m wheels. Nose is +Z.
// Authored for the `cars` showcase batch.
//
// The silhouette is a pure straight WEDGE of flat creased panels — never a
// three-box, never a pickup: an extremely low sharp chisel nose almost at the
// ground climbs one straight hood line to a laid-back windscreen and a short low
// roof cap, so the FRONT reads as a wide low letterbox (~1.7:1), not a box.
// Behind the low cabin the Esprit signature: two body-colour FLYING BUTTRESSES
// sweep from the roof sides down to the rear corners, with a slatted louvred
// engine cover set in the channel between them, falling to a sharp full-width
// Kamm tail. The cues: flat rectangular pop-up headlamps flush in the wedge
// nose, a black front bib spoiler, a thin triangular side-glass slot, low
// angular flank intakes ahead of the fat rear wheels, black slatted engine deck,
// a full-width black tail with a slatted grille and twin exhausts, and clean
// bright alloys. Body panels carry the "paint" recolour slot (Lotus white);
// `drive` spins the wheels and `hazards` blinks four indicators.

const PAINT = "#e8eae7"; // Lotus white — the "paint" slot default
const TRIM = "#141518"; // gloss black (slats, pillars, engine cover, bumpers)
const CLAD = "#1c1e21"; // lower valances, chin spoiler, rocker sills
const WELL = "#141518"; // wheel-well discs
const GLASS = "#1b2129"; // smoked side glazing (the thin slot)
const SCREEN = "#24303b"; // windscreen (a shade lighter than the tint)
const CABIN = "#0e0f12"; // interior block behind the glass
const MESH = "#0d0e10"; // intake mouths + engine grille mesh (near-black)
const PETAL = "#c9ced3"; // bright alloy spokes
const DISH = "#101114"; // dark wheel dish
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // pop-up headlamp glass
const AMBER = "#e8a13a";
const TAIL_RED = "#c41726"; // full-width tail lamp band
const CHROME = "#ccd1d6";
const EXHAUST = "#2b2e33"; // exhaust tip metal (dark gunmetal)
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres). LOW, WIDE, LONG — the belt sits at ~0.64 (just
// above the 0.62 wheel tops) and the roof caps at only ~1.05, while the body
// runs ~1.78 wide, so every square view reads emphatically wider than tall.
const WHEEL_R = 0.31;
const AXLE_F = 1.28;
const AXLE_R = -1.16; // wheelbase 2.44
const WHEEL_X_F = 0.8; // half track, front
const WHEEL_X_R = 0.82; // half track, rear (fatter tyres)

// The single wedge rake: the hood climbs on WEDGE_RAKE, the windscreen is only
// a little steeper, so nose -> hood -> windscreen -> roof read as one climbing
// line rather than a vertical wall. The buttress + deck slopes fall to the tail.
const WEDGE_RAKE = 0.2; // long flat hood on the wedge
const SCREEN_RAKE = 0.34; // laid-back windscreen continuing the wedge
const BUTTRESS_SLOPE = 0.3456; // flying-buttress sails, atan(0.36/1.05)
const REARWIN_SLOPE = 0.44; // slatted backlight panel falling to the deck
const DECK_SLOPE = 0.08; // short slatted engine deck, gently falling to the Kamm tail

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** One clean bright alloy: tyre, brake disc, a dark recessed dish, five slim
 *  bright spokes proud of the tyre face, a chrome centre cap. Every part shares
 *  `name` so the `drive` track spins them as one node about the axle. `side` is
 *  +1 for the right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, halfTrack: number, tyreW: number, z: number, name: string): Part[] {
  const y = WHEEL_R;
  const outer = halfTrack + tyreW / 2; // tyre outer face — stays proud of the well disc
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, tyreW, 18, TIRE, [side * halfTrack, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.13, 0.13, 0.03, 14, BRAKE, [side * (halfTrack - 0.05), y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.2, 0.2, 0.05, 18, DISH, [side * (outer - 0.055), y, z], [0, 0, Math.PI / 2], { name }),
  ];
  for (let k = 0; k < 5; k++) {
    parts.push(
      box(0.055, 0.34, 0.08, PETAL, [side * (outer - 0.02), y, z], [(k * 2 * Math.PI) / 5, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.05, 0.05, 0.03, 12, CHROME, [side * (outer - 0.002), y, z], [0, 0, Math.PI / 2], { name }));
  return parts;
}

/** The opened wheel well: a dark disc proud of the body side (the arch "cut")
 *  plus a body-coloured half-torus brow over the tyre. The [0, pi/2, 0] yaw
 *  puts the 0..pi arc exactly over the top half. */
function arch(side: 1 | -1, z: number, discX: number, discR: number, browR: number, browTube: number): Part[] {
  return [
    cyl(discR, discR, 0.04, 18, WELL, [side * discX, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(browR, browTube, 6, 18, Math.PI),
      color: PAINT,
      position: [side * (discX + 0.015), WHEEL_R, z],
      rotation: [0, Math.PI / 2, 0],
      slot: "paint",
    },
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Front pair flanks the nose bib; rear pair sits at the ends of the tail band.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.6, 0.34, 1.98], size: [0.16, 0.05, 0.04] },
  { name: "indicator-fr", pos: [0.6, 0.34, 1.98], size: [0.16, 0.05, 0.04] },
  { name: "indicator-rl", pos: [-0.72, 0.62, -1.98], size: [0.12, 0.06, 0.03] },
  { name: "indicator-rr", pos: [0.72, 0.62, -1.98], size: [0.12, 0.06, 0.03] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear (wide track — tyres planted at the corners) ---------------
  P.push(
    ...wheel(-1, WHEEL_X_F, 0.25, AXLE_F, "wheel-fl"), ...wheel(1, WHEEL_X_F, 0.25, AXLE_F, "wheel-fr"),
    ...wheel(-1, WHEEL_X_R, 0.3, AXLE_R, "wheel-rl"), ...wheel(1, WHEEL_X_R, 0.3, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.5, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2]));
  }
  for (const s of S) {
    P.push(...arch(s, AXLE_F, 0.9, 0.4, 0.35, 0.04)); // front arch (low flat brow)
    P.push(...arch(s, AXLE_R, 0.93, 0.42, 0.37, 0.045)); // rear arch, a touch wider
  }
  P.push(box(1.62, 0.14, 3.6, TRIM, [0, 0.18, -0.05])); // underbody pan (fills under the wedge)

  // -- Lower body: a very low WIDE tub, belt at ~0.64 -------------------------
  P.push(paint(1.78, 0.44, 3.5, [0, 0.44, -0.05])); // main tub, sill to belt line (top 0.66)

  // Low pointed WIDE chisel nose: a very low front face, a long flat hood on the
  // wedge rake, flat fender crests beside it. The hood line is unbroken.
  P.push(paint(1.64, 0.14, 0.12, [0, 0.32, 2.02])); // very low wide chisel prow face
  P.push(paint(1.46, 0.06, 1.4, [0, 0.47, 1.34], [WEDGE_RAKE, 0, 0])); // long flat hood on the wedge rake
  for (const s of S) P.push(paint(0.2, 0.08, 1.4, [s * 0.78, 0.5, 1.34], [WEDGE_RAKE, 0, 0])); // flat fender crests
  P.push(box(1.68, 0.12, 0.18, CLAD, [0, 0.16, 1.98])); // black front bib spoiler

  // -- Rear body sides: flat creased flanks, only mildly wider than the front --
  for (const s of S) {
    P.push(paint(0.16, 0.44, 1.9, [s * 0.81, 0.44, -0.85])); // flat rear flank (outer edge ~0.89)
  }

  // -- Greenhouse: a low glass wedge under a short flat roof cap ---------------
  // The windscreen is only a little steeper than the hood, so the wedge reads as
  // one climbing line. A narrow low cabin lets the wide body dominate.
  P.push(box(1.28, 0.05, 0.75, SCREEN, [0, 0.86, 0.42], [SCREEN_RAKE, 0, 0])); // laid-back windscreen on the wedge
  for (const s of S) P.push(paint(0.08, 0.07, 0.75, [s * 0.58, 0.86, 0.42], [SCREEN_RAKE, 0, 0])); // thin A-pillar rails
  P.push(paint(1.02, 0.05, 0.6, [0, 1.03, -0.2])); // low flat roof cap, the peak of the car (top 1.055)
  P.push(box(0.96, 0.26, 0.95, CABIN, [0, 0.68, 0.0])); // dark interior block under the glass
  for (const s of S) {
    P.push(box(0.03, 0.16, 0.6, GLASS, [s * 0.52, 0.86, -0.05], [0, 0, s * 0.12])); // thin side-glass slot
    P.push(box(0.05, 0.16, 0.06, TRIM, [s * 0.52, 0.86, -0.32], [0, 0, s * 0.12])); // B-pillar
  }

  // -- Flying buttresses: two body-colour sails from the roof to the rear -------
  // They sweep down-back from the roof sides into the rear corners; the slatted
  // engine cover drops into the channel between them (never a flat open deck).
  // Their tops sit at/below the roof (top-surface line (z,y): (-0.5,1.02) ->
  // (-1.5,0.66)), so from the front the white roof is cleanly the highest point.
  for (const s of S) {
    P.push(paint(0.16, 0.28, 1.12, [s * 0.44, 0.708, -0.953], [-BUTTRESS_SLOPE, 0, 0]));
  }

  // -- Slatted backlight + engine deck, dropped into the buttress channel ------
  // Every black slat rides BELOW the roof line so nothing black peeks over the
  // roof in the front/back views.
  P.push(box(0.94, 0.22, 1.0, MESH, [0, 0.5, -1.3])); // engine bay block under the cover
  // steep slatted backlight bridging the roof rear down to the deck
  P.push(box(0.86, 0.05, 0.34, TRIM, [0, 0.82, -0.68], [-REARWIN_SLOPE, 0, 0])); // backlight base panel
  for (let i = 0; i < 4; i++) {
    const z = -0.6 - 0.1 * i;
    const y = 0.93 + 0.44 * (z + 0.6); // rides down, kept clear below the roof line
    P.push(box(0.82, 0.02, 0.05, MESH, [0, y, z], [-REARWIN_SLOPE, 0, 0])); // backlight louvres
  }
  // flat slatted engine cover falling gently to the tail
  P.push(box(0.9, 0.05, 0.85, TRIM, [0, 0.72, -1.35], [-DECK_SLOPE, 0, 0])); // engine cover base
  for (let i = 0; i < 6; i++) {
    const z = -1.02 - 0.11 * i; // from -1.02 back to -1.57
    const y = 0.74 + 0.08 * (z + 0.92) + 0.03; // ride the falling slope
    P.push(box(0.86, 0.02, 0.05, MESH, [0, y, z], [-DECK_SLOPE, 0, 0])); // engine-cover slats
  }

  // -- Low angular flank intakes ahead of the rear wheels ----------------------
  for (const s of S) {
    P.push(box(0.05, 0.2, 0.5, MESH, [s * 0.89, 0.5, -0.5])); // low side intake mouth
    P.push(paint(0.06, 0.05, 0.55, [s * 0.87, 0.61, -0.5])); // scoop upper lip
    P.push(paint(0.06, 0.05, 0.55, [s * 0.87, 0.39, -0.5])); // scoop lower lip
    P.push(paint(0.06, 0.22, 0.06, [s * 0.87, 0.5, -0.24])); // scoop front wall
    P.push(box(0.04, 0.1, 2.4, CLAD, [s * 0.82, 0.28, -0.1])); // rocker sill (reaches both arches)
  }

  // -- Front fascia: flat rectangular pop-up lamps flush in the wedge nose ------
  for (const s of S) {
    P.push(box(0.34, 0.05, 0.16, TRIM, [s * 0.44, 0.44, 1.9], [WEDGE_RAKE, 0, 0])); // flush pop-up housing
    P.push(box(0.3, 0.03, 0.13, LAMP, [s * 0.44, 0.46, 1.91], [WEDGE_RAKE, 0, 0])); // rectangular lamp face
  }
  P.push(box(0.9, 0.06, 0.05, MESH, [0, 0.27, 2.07])); // low wide thin front intake slot
  P.push(cyl(0.04, 0.04, 0.02, 16, CHROME, [0, 0.42, 2.09], [Math.PI / 2, 0, 0])); // nose badge disc
  P.push(box(0.36, 0.09, 0.02, PLATE, [0, 0.24, 2.08])); // small low front plate

  // -- Rear fascia: sharp full-width Kamm tail ---------------------------------
  P.push(box(1.68, 0.4, 0.08, TRIM, [0, 0.52, -1.98])); // black rear Kamm panel
  P.push(box(1.24, 0.12, 0.05, TAIL_RED, [0, 0.62, -2.03])); // full-width tail lamp band
  for (let i = -2; i <= 2; i++) {
    P.push(box(0.02, 0.1, 0.02, TRIM, [i * 0.24, 0.62, -2.055])); // tail band dividers
  }
  // central slatted grille under the lamp band
  P.push(box(0.7, 0.14, 0.03, MESH, [0, 0.44, -2.03]));
  for (let i = -2; i <= 2; i++) {
    P.push(box(0.02, 0.11, 0.02, "#26282c", [i * 0.15, 0.44, -2.05]));
  }
  // twin dark gunmetal exhaust tips, centred low
  for (const dx of [-0.13, 0.13]) {
    P.push(cyl(0.05, 0.05, 0.09, 12, EXHAUST, [dx, 0.3, -2.06], [Math.PI / 2, 0, 0]));
  }
  P.push(box(1.4, 0.14, 0.12, CLAD, [0, 0.2, -1.96])); // rear diffuser valance
  P.push(box(0.36, 0.09, 0.02, PLATE, [0.44, 0.44, -2.06])); // small rear plate, offset to one side

  // -- Furniture: small mirrors firmly attached to the door tops ---------------
  for (const s of S) {
    P.push(box(0.12, 0.03, 0.04, TRIM, [s * 0.62, 0.7, 0.42])); // solid mirror stalk overlapping the body
    P.push(paint(0.06, 0.08, 0.09, [s * 0.72, 0.72, 0.42])); // mirror head
    P.push(box(0.012, 0.06, 0.07, GLASS, [s * 0.755, 0.72, 0.43])); // mirror glass, facing back
    P.push(box(0.02, 0.02, 0.11, CHROME, [s * 0.83, 0.5, -0.05])); // door handle
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X_R, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X_R, WHEEL_R, AXLE_R] },
];

export const lotusEsprit = defineModel({
  id: "lotus-esprit",
  name: "Lotus Esprit S1",
  batchId: "cars",
  tags: ["car", "lotus", "esprit", "supercar", "sports", "classic"],
  summary: "A Lotus Esprit S1 — the Giugiaro folded-paper wedge supercar at true scale, in Lotus white.",
  description:
    "A Lotus Esprit S1 (about 1977, the Giugiaro origami wedge) at real 1 unit " +
    "= 1 metre scale (about 4.19 long, 1.86 wide, an extremely low 1.11 tall, " +
    "2.44 wheelbase). The silhouette is a pure straight wedge of flat creased " +
    "panels: an extremely low sharp chisel nose almost at the ground climbs one " +
    "straight hood line to a laid-back windscreen and a short low roof cap, so " +
    "the front reads as a wide low letterbox rather than a box. Behind the low " +
    "cabin the Esprit signature is present: two body-colour flying buttresses " +
    "sweep from the roof sides down to the rear corners with a black slatted " +
    "louvred engine cover set in the channel between them, falling to a sharp " +
    "full-width Kamm tail. The cues are all there: flat rectangular pop-up " +
    "headlamps flush in the wedge nose, a black front bib spoiler, a thin " +
    "triangular side-glass slot, low angular flank intakes ahead of the rear " +
    "wheels, a black slatted engine deck, a full-width black tail with a red " +
    "lamp band, a slatted grille and twin exhausts, and clean bright alloys. " +
    "Body panels carry the 'paint' recolour slot (Lotus white); `drive` spins " +
    "the wheels and `hazards` blinks the four indicators.",
  author: "Fable 5",
  footprint: 2.25,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
